import { useEffect } from 'react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { applyFrostTier, resolveFrostTier, type FrostTier } from '@/utils/frostTier';

/**
 * Resolves the device's frost tier and writes `data-frost-tier` on <html> so
 * glass surfaces adapt to the device.
 *
 * Model: "downgrade always, upgrade behind the flag".
 * - The resolver runs for EVERYONE, so weak devices (few cores / low RAM /
 *   reduced-transparency / data-saver) always land on Tier 1 (flat) — the
 *   gama-baja protection is on by default, no rollout needed.
 * - The upgrade to glass (Tier 2/3) on capable devices is gated by FROST_TIERS:
 *   with the flag off the tier is capped at 1, so capable devices stay flat too
 *   until we open the flag. Re-resolves on a11y preference changes. Renders nothing.
 */
export const FrostTierController = (): null => {
  const { FROST_TIERS } = useFeatureFlags();

  useEffect(() => {
    const apply = () => {
      const { tier } = resolveFrostTier();
      const cap: FrostTier = FROST_TIERS ? 3 : 1;
      applyFrostTier(Math.min(tier, cap) as FrostTier);
    };
    apply();

    const queries = ['(prefers-reduced-transparency: reduce)', '(prefers-reduced-motion: reduce)'];
    const mqls = queries
      .map((q) => {
        try {
          const mql = window.matchMedia(q);
          mql.addEventListener('change', apply);
          return mql;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as MediaQueryList[];

    return () => mqls.forEach((mql) => mql.removeEventListener('change', apply));
  }, [FROST_TIERS]);

  return null;
};
